import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Copy, Loader2, ExternalLink } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import {
  fetchWorkspaceAdminContacts,
  type AdminContact,
} from '@/modules/core/data/accessDeniedRepository';

interface AccessRequestSectionProps {
  orgId?: string;
  orgName?: string;
  requesterEmail?: string | null;
  requesterName?: string | null;
}

function buildMessage(orgName: string, displayName: string, customReason: string) {
  const hasCustom = customReason.trim().length > 0;
  const lines = [
    'Bonjour,',
    '',
    `Je me permets de vous contacter car je ne dispose pas actuellement des permissions nécessaires pour accéder à certaines fonctionnalités du workspace "${orgName}".`,
    '',
    `Utilisateur concerné : ${displayName}`,
    '',
  ];

  if (hasCustom) {
    lines.push('Détail de la demande :', customReason.trim(), '');
  } else {
    lines.push(
      'Pourriez-vous vérifier mes droits d\'accès et, si cela est approprié, me les accorder afin que je puisse poursuivre mon travail ?',
      '',
    );
  }

  lines.push(
    'Je reste disponible si vous avez besoin d\'informations complémentaires.',
    '',
    'Cordialement,',
    displayName,
  );

  return lines.join('\n');
}

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
}

function formatAdminDisplay(a: AdminContact): string {
  if (!a.full_name) return a.email;
  const parts = a.full_name.trim().split(/\s+/);
  if (parts.length >= 2) {
    const formatted = parts.slice(0, -1).join(' ') + ' ' + parts[parts.length - 1].toUpperCase();
    return `${formatted} (${a.email})`;
  }
  return `${a.full_name} (${a.email})`;
}

export function AccessRequestSection({ orgId, orgName, requesterEmail, requesterName }: AccessRequestSectionProps) {
  const [admins, setAdmins] = useState<AdminContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [reason, setReason] = useState('');
  const confirmRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!orgId) { setAdmins([]); return; }
    let active = true;
    setLoading(true);
    fetchWorkspaceAdminContacts(orgId)
      .then((d) => { if (active) setAdmins(d); })
      .catch(() => { if (active) setAdmins([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [orgId]);

  const orgLabel = orgName || 'le workspace';
  const userDisplay = requesterName
    ? `${requesterName} (${requesterEmail || ''})`
    : (requesterEmail || 'un utilisateur');
  const adminEmails = admins.map((a) => a.email).join(',');
  const adminSummary = admins
    .map(formatAdminDisplay)
    .join(' \u2022 ');

  const getMessage = () => buildMessage(orgLabel, userDisplay, reason);

  // ── Copy message ──────────────────────────────────────────────────
  const handleCopy = async () => {
    setSubmitting(true);
    await copyText(getMessage());
    setSent(true);
    setSubmitting(false);
    setTimeout(() => confirmRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 100);
    toast({
      title: 'Message copié',
      description: 'Collez-le dans un email ou un message destiné à votre administrateur.',
    });
  };

  // ── Open mailto ───────────────────────────────────────────────────
  const handleEmail = async () => {
    if (!adminEmails) {
      await handleCopy();
      return;
    }

    setSubmitting(true);

    const subject = encodeURIComponent(`Demande d'accès — ${orgLabel}`);
    const body = encodeURIComponent(getMessage());
    const url = `mailto:${adminEmails}?subject=${subject}&body=${body}`;

    // Create a real <a> element and click it — works in iframes/sandboxes
    // where window.open and window.location.href are blocked.
    const link = document.createElement('a');
    link.href = url;
    link.target = '_self';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Also copy as safety net
    await copyText(getMessage());
    setSent(true);
    setSubmitting(false);
    setTimeout(() => confirmRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 100);
    toast({
      title: 'Email en cours d\'ouverture',
      description: 'Le message a aussi été copié dans le presse-papiers si besoin.',
    });
  };

  return (
    <div className="flex flex-col gap-2 mt-4">
      {!showForm ? (
        <Button
          variant="outline"
          onClick={() => { setShowForm(true); setSent(false); }}
          disabled={!orgId}
          className="w-full"
        >
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ExternalLink className="mr-2 h-4 w-4" />
          )}
          {loading ? 'Recherche des administrateurs\u2026' : 'Contacter un administrateur'}
        </Button>
      ) : (
        <div className="rounded-md border bg-muted/30 p-3 text-left space-y-3">
          {/* Header */}
          <div className="space-y-1">
            <p className="text-sm font-medium">Demander l'accès</p>
            <p className="text-xs text-muted-foreground">
              Un message pré-rédigé sera envoyé à l'administrateur.
              Vous pouvez le personnaliser ci-dessous ou l'envoyer tel quel.
            </p>
          </div>

          {/* Optional custom reason */}
          <Textarea
            value={reason}
            onChange={(e) => { setReason(e.target.value); setSent(false); }}
            placeholder="(Optionnel) Personnalisez votre message…"
            className="min-h-[70px] resize-none"
          />

          {/* Admin contacts */}
          {!loading && admins.length > 0 && (
            <div className="rounded-md border bg-background p-3">
              <p className="text-xs font-medium">
                {admins.length === 1 ? 'Administrateur détecté' : 'Administrateurs détectés'}
              </p>
              <p className="text-xs text-muted-foreground break-words mt-1">{adminSummary}</p>
            </div>
          )}

          {!loading && admins.length === 0 && (
            <div className="rounded-md border bg-background p-3">
              <p className="text-xs text-muted-foreground">
                Aucun administrateur détecté. Vous pouvez copier le message et l'envoyer manuellement.
              </p>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              onClick={handleEmail}
              disabled={submitting || loading || !admins.length}
              className="w-full sm:flex-1"
            >
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="mr-2 h-4 w-4" />
              )}
              Envoyer par email
            </Button>
            <Button
              variant="outline"
              onClick={handleCopy}
              disabled={submitting}
              className="w-full sm:flex-1"
            >
              <Copy className="mr-2 h-4 w-4" />
              Copier le message
            </Button>
          </div>

          {/* Confirmation */}
          {sent && (
            <div ref={confirmRef} className="rounded-md border bg-background p-3">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium">Message copié</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Le message a été copié dans le presse-papiers et Outlook ou votre messagerie devrait s'ouvrir automatiquement.
                    Si elle ne s'ouvre pas, faites un copier-coller du message dans votre messagerie.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
